import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, Circle, Plus, Trash2, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_STYLES = {
  'Fiscales': { border: 'border-l-red-500', bg: 'bg-red-50/30', badge: 'bg-red-100 text-red-800' },
  'Contabilidad': { border: 'border-l-blue-500', bg: 'bg-blue-50/30', badge: 'bg-blue-100 text-blue-800' },
  'Electrónicos': { border: 'border-l-amber-500', bg: 'bg-amber-50/30', badge: 'bg-amber-100 text-amber-800' },
  'Informes a Gerencia': { border: 'border-l-emerald-500', bg: 'bg-emerald-50/30', badge: 'bg-emerald-100 text-emerald-800' },
};

const ClientActivityConfig = ({ clientId }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newActs, setNewActs] = useState({});

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/actividades-config`);
      setConfig(data);
    } catch (err) {
      console.error('Error loading activity config');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async (updatedCategorias) => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/clients/${clientId}/actividades-config`, {
        categorias: updatedCategorias,
      });
    } catch (err) {
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleActivity = (catIdx, actIdx) => {
    setConfig(prev => {
      const cats = prev.categorias.map((c, ci) => {
        if (ci !== catIdx) return c;
        const acts = c.actividades.map((a, ai) => ai === actIdx ? { ...a, activo: !a.activo } : a);
        return { ...c, actividades: acts };
      });
      saveConfig(cats);
      return { ...prev, categorias: cats };
    });
  };

  const removeActivity = (catIdx, actIdx) => {
    setConfig(prev => {
      const cats = prev.categorias.map((c, ci) => {
        if (ci !== catIdx) return c;
        return { ...c, actividades: c.actividades.filter((_, ai) => ai !== actIdx) };
      });
      saveConfig(cats);
      return { ...prev, categorias: cats };
    });
  };

  const addActivity = (catIdx) => {
    const catKey = config.categorias[catIdx].categoria;
    const name = (newActs[catKey] || '').trim();
    if (!name) return;
    setConfig(prev => {
      const cats = prev.categorias.map((c, ci) => {
        if (ci !== catIdx) return c;
        return { ...c, actividades: [...c.actividades, { nombre: name, activo: true }] };
      });
      saveConfig(cats);
      return { ...prev, categorias: cats };
    });
    setNewActs(prev => ({ ...prev, [catKey]: '' }));
  };

  if (loading) return <p className="text-sm text-slate-500 py-4">Cargando actividades...</p>;
  if (!config) return <p className="text-sm text-slate-500 py-4">No se pudo cargar la configuración</p>;

  const totalActivas = config.categorias.reduce((sum, c) => sum + c.actividades.filter(a => a.activo).length, 0);
  const totalActividades = config.categorias.reduce((sum, c) => sum + c.actividades.length, 0);

  return (
    <div data-testid="client-activity-config">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Actividades del Cierre Mensual</h3>
          <p className="text-xs text-slate-500 mt-0.5">{totalActivas} de {totalActividades} actividades activas</p>
        </div>
        {saving && <span className="text-xs text-blue-600 flex items-center gap-1"><Save className="w-3 h-3 animate-spin" /> Guardando...</span>}
      </div>

      <div className="space-y-4">
        {config.categorias.map((cat, catIdx) => {
          const style = CAT_STYLES[cat.categoria] || { border: 'border-l-slate-400', bg: 'bg-slate-50/30', badge: 'bg-slate-100 text-slate-800' };
          const activas = cat.actividades.filter(a => a.activo).length;
          return (
            <div key={cat.categoria} className={`border-l-4 ${style.border} ${style.bg} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${style.badge}`}>{cat.categoria}</span>
                <span className="text-xs text-slate-500">{activas}/{cat.actividades.length}</span>
                {cat.categoria === 'Fiscales' && <span className="text-xs text-slate-400 italic">Según impuestos configurados</span>}
              </div>

              <div className="space-y-1">
                {cat.actividades.map((act, actIdx) => (
                  <div key={actIdx} className="flex items-center gap-2 group">
                    <button
                      onClick={() => toggleActivity(catIdx, actIdx)}
                      className="flex items-center gap-2 flex-1 text-left text-sm px-3 py-1.5 rounded transition-all hover:bg-white/60"
                      data-testid={`client-act-toggle-${catIdx}-${actIdx}`}
                    >
                      {act.activo
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        : <Circle className="w-4 h-4 text-slate-300 flex-shrink-0" />
                      }
                      <span className={act.activo ? 'text-slate-800' : 'text-slate-400 line-through'}>{act.nombre}</span>
                    </button>
                    {!act.es_fiscal && (
                      <button
                        onClick={() => removeActivity(catIdx, actIdx)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add custom activity */}
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  placeholder="Agregar actividad..."
                  value={newActs[cat.categoria] || ''}
                  onChange={(e) => setNewActs(prev => ({ ...prev, [cat.categoria]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && addActivity(catIdx)}
                  className="flex-1 text-sm border border-slate-200 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#1F3864]"
                />
                <button
                  onClick={() => addActivity(catIdx)}
                  className="px-2 py-1 bg-[#1F3864] text-white text-xs rounded hover:bg-[#172b4d] transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientActivityConfig;
